import { useState, useEffect, useMemo, useCallback, useRef, useId } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ArrowLeft, CalendarIcon, ChevronDown, Pencil, Check, ExternalLink, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { format, parseISO, parse, addHours, getDay } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import DynamicTimeSlots from "@/components/listing_booking_page/DynamicTimeSlots";
import { useDynamicPricing } from "@/hooks/useDynamicPricing";
import { BookingAccessRecovery } from "@/components/booking/BookingAccessRecovery";
import { getPublicSlotUsage, isPastBookingCutoff, readBookingError } from "@/lib/publicBooking";

interface ScheduleData {
  weekly: Record<string, string[]>;
  exceptions: { date: string; slots: string[]; isClosed: boolean }[];
}

interface CustomQuestion {
  id: string;
  question: string;
  type: "text" | "dropdown" | "radio";
  required: boolean;
  options?: string[];
}

interface Booking {
  booking_id: string;
  listing_id: string;
  booking_date: string;
  booking_time: string;
  customer_name: string;
  customer_email: string;
  customer_phone: string | null;
  status: string;
  number_of_pax?: number;
  custom_field_responses?: Record<string, any>;
  listings: {
    name: string;
    price: number;
    duration: string;
    description?: string;
    images?: string[];
    spots?: number;
    schedule?: ScheduleData | null;
    custom_questions?: CustomQuestion[];
    businesses?: {
      email: string;
      slug: string;
    };
  };
}

const DAY_NAMES = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

const formatTimeTo12h = (time24: string): string => {
  const [hStr, mStr] = time24.split(":");
  let h = parseInt(hStr) || 0;
  const m = mStr || "00";
  const period = h >= 12 ? "PM" : "AM";
  if (h === 0) h = 12;
  else if (h > 12) h -= 12;
  return `${h}:${m} ${period}`;
};

const parseTime24ToMinutes = (timeStr: string): number => {
  const [hStr, mStr] = timeStr.split(":");
  return (parseInt(hStr) || 0) * 60 + (parseInt(mStr) || 0);
};

const EditableField = ({
  label,
  value,
  onChange,
  type = "text",
  placeholder,
  isDisabled,
  error,
}: {
  label: string;
  value: string | number;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
  isDisabled?: boolean;
  error?: string | null;
}) => {
  const [editing, setEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const fieldId = useId();

  const handleEditClick = () => {
    setEditing(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  return (
    <div className={cn("space-y-1.5 group", isDisabled && "opacity-50")}>
      <Label htmlFor={fieldId} className={cn(error && "text-destructive")}>
        {label}
      </Label>
      <div className="flex items-center gap-2">
        <Input
          id={fieldId}
          ref={inputRef}
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          readOnly={!editing}
          disabled={isDisabled}
          aria-invalid={!!error}
          className={cn(error && "border-destructive focus-visible:ring-destructive")}
          onKeyDown={(e) => {
            if (e.key === "Enter") setEditing(false);
          }}
          onBlur={() => setEditing(false)}
        />
        <button
          type="button"
          onClick={editing ? () => setEditing(false) : handleEditClick}
          className={cn(
            "shrink-0 h-9 w-9 rounded-md flex items-center justify-center transition-colors border",
            editing
              ? "bg-primary/10 text-primary border-primary/20"
              : "text-muted-foreground border-transparent opacity-60 group-hover:opacity-100 hover:bg-muted",
            isDisabled && "hidden"
          )}
          disabled={isDisabled}
          onMouseDown={(e) => {
            if (!editing) e.preventDefault();
          }}
          aria-label={editing ? "Save" : "Edit"}
        >
          {editing ? <Check className="w-4 h-4" /> : <Pencil className="w-3.5 h-3.5" />}
        </button>
      </div>
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
};

const ModifyBooking = () => {
  const { bookingId } = useParams<{ bookingId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const accessToken = searchParams.get("token") ?? "";
  const [booking, setBooking] = useState<Booking | null>(null);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [timeOpen, setTimeOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [selectedTime, setSelectedTime] = useState<string | undefined>(undefined);
  const [isPassed, setIsPassed] = useState(false);
  const [isWithin24h, setIsWithin24h] = useState(false);
  const [pastDateModalOpen, setPastDateModalOpen] = useState(false);
  const [lessThan24hModalOpen, setLessThan24hModalOpen] = useState(false);
  const [seriesModalOpen, setSeriesModalOpen] = useState(false);
  const [appointmentModalOpen, setAppointmentModalOpen] = useState(false);
  const [successModalOpen, setSuccessModalOpen] = useState(false);

  // Editable fields
  const [customerName, setCustomerName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [numberOfPax, setNumberOfPax] = useState<number>(1);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [customFieldResponses, setCustomFieldResponses] = useState<Record<string, any>>({});
  const [initialCustomFieldResponses, setInitialCustomFieldResponses] = useState<Record<string, any>>({});
  const [nameError, setNameError] = useState<string | null>(null);

  // Booking counts per time slot for capacity enforcement
  const [slotBookedPax, setSlotBookedPax] = useState<Record<string, number>>({});

  const hasChanges = useMemo(() => {
    if (!booking) return false;
    const origDate = parseISO(booking.booking_date);
    const dateChanged = selectedDate?.getTime() !== origDate.getTime();
    const timeChanged = selectedTime !== booking.booking_time;
    const nameChanged = customerName !== booking.customer_name;
    const emailChanged = customerEmail !== booking.customer_email;
    const phoneChanged = customerPhone !== (booking.customer_phone || "");
    const paxChanged = numberOfPax !== ((booking as any).number_of_pax || 1);
    const customFieldsChanged = JSON.stringify(customFieldResponses) !== JSON.stringify(initialCustomFieldResponses);
    return dateChanged || timeChanged || nameChanged || emailChanged || phoneChanged || paxChanged || customFieldsChanged;
  }, [booking, selectedDate, selectedTime, customerName, customerEmail, customerPhone, numberOfPax, customFieldResponses, initialCustomFieldResponses]);

  useEffect(() => {
    const fetchBooking = async () => {
      if (!bookingId) return;

      // Booking comes from the manage-booking fn. The bookings table is no
      // longer publicly readable.
      const { data: lookup, error } = await supabase.functions.invoke("manage-booking", {
        body: { action: "get", access_token: accessToken },
      });
      const data = lookup?.booking;

      if (!error && data) {
        // Parse schedule from JSONB
        const rawListing = (data as any).listings;
        const schedule = rawListing?.schedule && typeof rawListing.schedule === "object"
          ? (rawListing.schedule as unknown as ScheduleData)
          : null;

        const bookingData = {
          ...data,
          listings: {
            ...rawListing,
            schedule,
          },
        } as any;

        setBooking(bookingData);
        setSelectedDate(parseISO(data.booking_date));
        setSelectedTime(data.booking_time);
        setCustomerName(data.customer_name);
        setCustomerEmail(data.customer_email);
        setCustomerPhone(data.customer_phone || "");
        setNumberOfPax((data as any).number_of_pax || 1);

        // Initialize custom field responses
        const responses = (data as any).custom_field_responses || {};
        setCustomFieldResponses(responses);
        setInitialCustomFieldResponses(responses);

        try {
          const bookingDateTime = parse(`${data.booking_date} ${data.booking_time}`, "yyyy-MM-dd h:mm a", new Date());
          const now = new Date();

          // Appointment bookings can't be self-rescheduled here. The weekly
          // grid doesn't apply and reschedule needs resource availability.
          // Point the customer to cancel-and-rebook (or contacting the team).
          if (rawListing?.booking_mode === "appointment" && data.status !== "cancelled") {
            setAppointmentModalOpen(true);
          }

          if (bookingDateTime < now) {
            setIsPassed(true);
            setPastDateModalOpen(true);
          } else if (data.series_id) {
            // For series bookings, show the series-specific modal.
            // We also set isWithin24h if applicable for state completeness.
            if (bookingDateTime < addHours(now, 24)) setIsWithin24h(true);
            setSeriesModalOpen(true);
          } else if (bookingDateTime < addHours(now, 24)) {
            setIsWithin24h(true);
            setLessThan24hModalOpen(true);
          }
        } catch (e) {
          console.error("Error parsing booking date and time:", e);
        }
      } else {
        toast.error("Booking not found");
      }
      setLoading(false);
    };

    fetchBooking();
  }, [bookingId, accessToken]);

  // Parse schedule data from listing
  const schedule = useMemo<ScheduleData | null>(() => {
    if (!booking?.listings?.schedule) return null;
    const s = booking.listings.schedule;
    if (s.weekly && typeof s.weekly === "object") return s;
    return null;
  }, [booking?.listings?.schedule]);

  // Get closed/exception dates for calendar blocking
  const closedDates = useMemo<Set<string>>(() => {
    const set = new Set<string>();
    if (!schedule) return set;
    for (const exc of schedule.exceptions) {
      if (exc.isClosed) set.add(exc.date);
    }
    return set;
  }, [schedule]);

  // Check if a date has no time slots configured
  const isDateDisabled = useCallback((date: Date): boolean => {
    // Past dates
    if (date < new Date(new Date().setHours(0, 0, 0, 0))) return true;

    const dateStr = format(date, "yyyy-MM-dd");

    // Closed exception dates
    if (closedDates.has(dateStr)) return true;

    if (!schedule) return false;

    // Check if there's an exception with custom slots for this date
    const exception = schedule.exceptions.find(e => e.date === dateStr);
    if (exception) {
      return exception.slots.length === 0;
    }

    // Check weekly schedule for this day
    const dayName = DAY_NAMES[getDay(date)];
    const daySlots = schedule.weekly[dayName];
    if (!daySlots || daySlots.length === 0) return true;

    return false;
  }, [schedule, closedDates]);

  // Fetch booking counts for the selected date+listing to enforce capacity
  useEffect(() => {
    const fetchBookingCounts = async () => {
      if (!booking || !selectedDate) {
        setSlotBookedPax({});
        return;
      }

      const dateStr = format(selectedDate, "yyyy-MM-dd");
      try {
        const data = await getPublicSlotUsage(booking.listing_id, [dateStr], {
          excludeBooking: booking.booking_id,
          accessToken,
        });
        const counts: Record<string, number> = {};
        for (const b of data) {
          const time = b.booking_time;
          counts[time] = (counts[time] || 0) + (b.number_of_pax || 1);
        }
        setSlotBookedPax(counts);
      } catch (error) {
        console.error("Failed to fetch booking counts:", error);
        setSlotBookedPax({});
      }
    };

    fetchBookingCounts();
  }, [booking?.listing_id, booking?.booking_id, selectedDate, accessToken]);

  // The listing's online-booking cut-off. Read on its own so the page keeps
  // working if the column isn't there; the server enforces it either way.
  const [cutoffHours, setCutoffHours] = useState<number | null>(null);
  useEffect(() => {
    if (!booking?.listing_id) return;
    let cancelled = false;
    void (supabase as any)
      .from("listings")
      .select("booking_cutoff_hours")
      .eq("listing_id", booking.listing_id)
      .maybeSingle()
      .then(({ data, error }: { data: { booking_cutoff_hours?: number | null } | null; error: unknown }) => {
        if (!cancelled && !error) setCutoffHours(data?.booking_cutoff_hours ?? null);
      });
    return () => { cancelled = true; };
  }, [booking?.listing_id]);

  // Build time slots dynamically from schedule + capacity
  const timeSlots = useMemo(() => {
    if (!selectedDate || !schedule) return [];

    const dateStr = format(selectedDate, "yyyy-MM-dd");
    const dayName = DAY_NAMES[getDay(selectedDate)];

    // Check for date exception override
    const exception = schedule.exceptions.find(e => e.date === dateStr);

    let rawSlots: string[];
    if (exception) {
      if (exception.isClosed) return [];
      rawSlots = exception.slots;
    } else {
      rawSlots = schedule.weekly[dayName] || [];
    }

    if (rawSlots.length === 0) return [];

    const now = new Date();
    const isToday = selectedDate.toDateString() === now.toDateString();
    const currentMinutes = now.getHours() * 60 + now.getMinutes();
    const maxSpots = booking?.listings?.spots ?? 0;

    return rawSlots
      .slice()
      .sort()
      .map(time24 => {
        const time12 = formatTimeTo12h(time24);
        const slotMinutes = parseTime24ToMinutes(time24);
        const isPast = isToday && slotMinutes <= currentMinutes;
        // Inside the owner's online-booking cut-off.
        const isClosed = !isPast && isPastBookingCutoff(dateStr, time24, cutoffHours);

        // Capacity: remaining spots for this slot
        const bookedPax = slotBookedPax[time12] || slotBookedPax[time24] || 0;
        const remainingSpots = Math.max(0, maxSpots - bookedPax);

        const isCurrentBookingDate = booking && selectedDate && format(selectedDate, "yyyy-MM-dd") === booking.booking_date;
        const isCurrentBookingTime = time12 === booking?.booking_time;
        const isCurrentBookingSlot = Boolean(isCurrentBookingDate && isCurrentBookingTime);

        return {
          time: time12,
          spots: remainingSpots,
          isPast,
          isClosed: isClosed && !isCurrentBookingSlot,
          isCurrentBookingSlot,
        };
      });
  }, [selectedDate, schedule, booking?.listings?.spots, slotBookedPax, booking, cutoffHours]);

  const { slotPricing } = useDynamicPricing(
    booking?.listing_id,
    booking?.listings?.price ?? 0,
    timeSlots,
    selectedDate
  );

  const handleDateSelect = (date: Date | undefined) => {
    setSelectedDate(date);
    setSelectedTime(undefined);
  };

  // Validate email format
  const validateEmail = (email: string): string | null => {
    if (!email.trim()) return "Email is required";
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return "Please enter a valid email (e.g. you@gmail.com)";
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!booking || !selectedDate || !selectedTime) {
      toast.error("Please select a new date and time");
      return;
    }

    // Validate name
    if (!customerName.trim()) {
      setNameError("Name is required");
      toast.error("Name cannot be empty");
      return;
    }

    // Validate email
    const emailErr = validateEmail(customerEmail);
    if (emailErr) {
      setEmailError(emailErr);
      toast.error(emailErr);
      return;
    }

    // Validate required custom fields
    const customQuestions = booking.listings?.custom_questions || [];
    for (const q of customQuestions) {
      if (q.required && (!customFieldResponses[q.id] || customFieldResponses[q.id] === "")) {
        toast.error(`Please fill in: ${q.question}`);
        return;
      }
    }

    setIsSubmitting(true);
    const isSeriesSubmit = !!(booking as any).series_id;
    const updatePayload: Record<string, any> = {
      customer_name: customerName,
      customer_email: customerEmail,
      customer_phone: customerPhone,
      number_of_pax: numberOfPax,
      custom_field_responses: customFieldResponses,
      status: "confirmed",
    };
    if (!isSeriesSubmit) {
      updatePayload.booking_date = format(selectedDate, "yyyy-MM-dd");
      updatePayload.booking_time = selectedTime;
    }
    // Writes go through the manage-booking fn (the public UPDATE policy on
    // bookings is gone); it whitelists fields + keeps series date/time locked.
    const { data, error } = await supabase.functions.invoke("manage-booking", {
      body: { action: "modify", access_token: accessToken, patch: updatePayload },
    });

    if (error) {
      // Show the server's reason (e.g. inside the booking cut-off) when it gives one.
      const refusal = await readBookingError(error, "Failed to modify booking. Please try again.");
      toast.error(refusal.message);
      setIsSubmitting(false);
    } else {
      toast.success("Booking modified successfully!");
      if (typeof data?.access_token === "string") {
        const next = new URLSearchParams(searchParams);
        next.set("token", data.access_token);
        setSearchParams(next, { replace: true });
      }
      setSuccessModalOpen(true);
      setIsSubmitting(false);
    }
  };

  if (!accessToken) return <BookingAccessRecovery bookingId={bookingId} />;

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!booking) return <BookingAccessRecovery bookingId={bookingId} />;

  const { listings } = booking;
  const imageUrl = listings.images && listings.images.length > 0 ? listings.images[0] : null;

  const isCancelled = booking.status === 'cancelled';
  // Recurring series rows are fully read-only on this page. Modifying any
  // single occurrence (date/time, pax, notes) breaks the recurrence model,
  // so the only supported action is cancel-and-rebook. Everything else is
  // disabled and the customer gets an explanatory banner.
  const isAppointmentBooking = (booking as any)?.listings?.booking_mode === "appointment";
  const isFormDisabled = isPassed || isWithin24h || isCancelled || !!(booking as any).series_id || isAppointmentBooking;
  // Series occurrences: date/time edits are forbidden because changing the
  // date breaks the recurring pattern. The customer can still update pax,
  // notes, and custom-field answers. To change a date, they cancel that
  // occurrence (>24h ahead = full refund) and book a new one-off.
  const isSeries = !!(booking as any).series_id;
  const isScheduleLocked = isFormDisabled || isSeries;

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-background/95 backdrop-blur-sm border-b">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-center">
          <div className="flex items-center gap-2">
            <img
              src="/dibs_assets/red_logo_with_transparent_bg.png"
              alt="Dibs"
              className="h-8 object-contain"
            />
            <span className="text-primary font-sans font-bold text-2xl tracking-tight">Dibs</span>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8 md:py-12 flex flex-col gap-6">
        <div>
          <h1 className="text-2xl font-heading font-bold text-foreground">Modify booking</h1>
          <p className="text-muted-foreground mt-1 text-sm">Review and update the schedule or guest information.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-6 items-start">

          {/* LEFT: Listing Card */}
          <div className="md:col-span-2 border rounded-xl shadow-sm bg-card overflow-hidden">
            {imageUrl ? (
              <div className="aspect-video w-full bg-muted">
                <img src={imageUrl} alt={listings.name} className="w-full h-full object-cover" />
              </div>
            ) : (
              <div className="aspect-video w-full bg-muted flex items-center justify-center">
                <span className="text-muted-foreground text-sm">No image available</span>
              </div>
            )}

            <div className="p-5 flex flex-col gap-3">
              <div className="flex items-start justify-between gap-3">
                <h2 className="text-lg font-bold leading-tight flex-1">{listings.name}</h2>
                <Link
                  to={`/listing/${booking.listing_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="shrink-0 p-1.5 bg-muted/50 rounded-md hover:bg-muted text-muted-foreground hover:text-primary transition-colors"
                  aria-label="Open listing in new window"
                >
                  <ExternalLink className="w-4 h-4" />
                </Link>
              </div>

              {listings.description && (
                <p className="text-sm text-muted-foreground line-clamp-4">
                  {listings.description}
                </p>
              )}
            </div>
          </div>

          {/* RIGHT: Booking Details */}
          <div className={`md:col-span-3 border rounded-xl shadow-xl bg-card overflow-hidden flex flex-col relative ${isCancelled ? 'border-muted' : ''}`}>
            <div className="p-6 space-y-6 flex-1">
              {isSeries && !isCancelled && (
                <div className="rounded-lg border border-amber-300/40 bg-amber-50 dark:bg-amber-950/20 p-3 text-sm">
                  <span className="font-medium text-foreground">This booking is part of a recurring series and can't be modified.</span>{" "}
                  <span className="text-muted-foreground">
                    Changing any field on a single occurrence — date, time, pax, notes, anything — would break the recurrence schedule. If you need a different time or guest count, please cancel this occurrence (or the series) and book again. All other modification options have been disabled below.
                  </span>
                </div>
              )}

              {/* Schedule Selection */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-medium uppercase tracking-wider text-muted-foreground">Schedule</h3>
                  {isCancelled && (
                    <span className="px-2.5 py-1 uppercase text-[10px] font-bold tracking-widest rounded-full bg-destructive text-destructive-foreground">Cancelled</span>
                  )}
                </div>

                <div className="grid gap-4">
                  <div className="space-y-1.5">
                    <Label>Date</Label>
                    <button
                      type="button"
                      onClick={() => {
                        setCalendarOpen(!calendarOpen);
                        if (!calendarOpen) setTimeOpen(false);
                      }}
                      className={cn(
                        "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-colors hover:bg-muted/50",
                        calendarOpen && "ring-2 ring-ring ring-offset-2 bg-muted/50",
                        isScheduleLocked && "opacity-50 cursor-not-allowed hover:bg-transparent"
                      )}
                      disabled={isScheduleLocked}
                    >
                      <span className={cn(!selectedDate && "text-muted-foreground")}>
                        {selectedDate ? format(selectedDate, "dd/MM/yyyy") : "Select date"}
                      </span>
                      <ChevronDown className={cn(
                        "w-4 h-4 text-muted-foreground transition-transform duration-200",
                        calendarOpen && "rotate-180"
                      )} />
                    </button>

                    {calendarOpen && (
                      <div className="p-3 bg-background border rounded-xl mt-2 animate-in fade-in slide-in-from-top-2 duration-200 shadow-lg z-10 relative">
                        <Calendar
                          mode="single"
                          selected={selectedDate}
                          onSelect={(date) => {
                            handleDateSelect(date);
                            if (date) {
                              setCalendarOpen(false);
                              setTimeOpen(true);
                            }
                          }}
                          disabled={isDateDisabled}
                          showOutsideDays={false}
                          className="w-full"
                          classNames={{
                            months: "w-full",
                            month: "w-full space-y-4",
                            table: "w-full border-collapse space-y-1",
                            head_row: "flex w-full",
                            head_cell: "text-muted-foreground rounded-md flex-1 font-normal text-[0.8rem]",
                            row: "flex w-full mt-2",
                            cell: "text-center text-sm p-0 relative focus-within:relative focus-within:z-20 flex-1",
                            day: "h-9 w-full p-0 font-normal aria-selected:opacity-100 hover:bg-accent hover:text-accent-foreground rounded-md",
                            day_selected: "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
                            day_today: "border border-primary text-primary bg-transparent font-bold",
                            day_outside: "text-muted-foreground opacity-50",
                            day_disabled: "text-muted-foreground opacity-50",
                            nav: "space-x-1 flex items-center",
                            nav_button: "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 border rounded-md flex items-center justify-center",
                            nav_button_previous: "absolute left-2",
                            nav_button_next: "absolute right-2",
                            caption: "flex justify-center pt-1 relative items-center w-full",
                          }}
                        />
                      </div>
                    )}
                  </div>

                  <div className="space-y-1.5">
                    <Label>Time</Label>
                    <button
                      type="button"
                      onClick={() => {
                        if (selectedDate) {
                          setTimeOpen(!timeOpen);
                          if (!timeOpen) setCalendarOpen(false);
                        }
                      }}
                      className={cn(
                        "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-colors hover:bg-muted/50",
                        !selectedDate && "opacity-50 cursor-not-allowed",
                        timeOpen ? "ring-2 ring-ring ring-offset-2 bg-muted/50" : "",
                        isScheduleLocked && "opacity-50 cursor-not-allowed hover:bg-transparent"
                      )}
                      disabled={!selectedDate || isScheduleLocked}
                    >
                      <span className={cn(!selectedTime && "text-muted-foreground")}>
                        {selectedTime ? selectedTime : "Select time"}
                      </span>
                      <ChevronDown className={cn(
                        "w-4 h-4 text-muted-foreground transition-transform duration-200",
                        timeOpen && "rotate-180"
                      )} />
                    </button>

                    {timeOpen && selectedDate && (
                      <div className="p-4 bg-background border rounded-xl mt-2 animate-in fade-in slide-in-from-top-2 duration-200 shadow-lg z-10 relative">
                        <DynamicTimeSlots
                          slots={slotPricing}
                          selectedTime={selectedTime}
                          onSelectTime={(time) => {
                            setSelectedTime(time);
                            setTimeOpen(false);
                          }}
                        />
                        {cutoffHours && (
                          <p className="mt-2 text-center text-xs text-muted-foreground">
                            Online booking closes {cutoffHours} hour{cutoffHours === 1 ? "" : "s"} before each session.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Editable Guest Information */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium uppercase tracking-wider text-muted-foreground">Guest information</h3>
                <div className={cn("grid gap-4 sm:grid-cols-2", isCancelled && "opacity-70")}>
                  <EditableField
                    label="Name"
                    value={customerName}
                    onChange={(v) => {
                      setCustomerName(v);
                      if (nameError) setNameError(null);
                    }}
                    placeholder="Customer name"
                    isDisabled={isFormDisabled}
                    error={nameError}
                  />
                  <div className={cn("space-y-1.5", isFormDisabled && "opacity-50")}>
                    <Label>Pax</Label>
                    <Input value={numberOfPax} readOnly disabled />
                  </div>
                  <EditableField
                    label="Email"
                    value={customerEmail}
                    onChange={(v) => {
                      setCustomerEmail(v);
                      if (emailError) setEmailError(null);
                    }}
                    type="email"
                    placeholder="email@example.com"
                    isDisabled={isFormDisabled}
                    error={emailError}
                  />
                  <EditableField
                    label="Phone (optional)"
                    value={customerPhone}
                    onChange={setCustomerPhone}
                    type="tel"
                    placeholder="+1 --- --- ----"
                    isDisabled={isFormDisabled}
                  />
                </div>
              </div>

              {/* Custom Fields */}
              {booking.listings?.custom_questions && booking.listings.custom_questions.length > 0 && (
                <div className="space-y-4">
                  <h3 className="text-sm font-medium uppercase tracking-wider text-muted-foreground">Additional information</h3>
                  <div className={cn("grid gap-4", isCancelled && "opacity-70")}>
                    {booking.listings.custom_questions.map((q) => (
                      <div key={q.id} className="space-y-1.5">
                        <Label htmlFor={`modify-q-${q.id}`}>
                          {q.question}
                          {q.required && <span className="text-destructive ml-0.5">*</span>}
                        </Label>

                        {q.type === "text" && (
                          <Input
                            id={`modify-q-${q.id}`}
                            type="text"
                            value={customFieldResponses[q.id] || ""}
                            onChange={(e) => setCustomFieldResponses(prev => ({ ...prev, [q.id]: e.target.value }))}
                            placeholder="Your answer"
                            required={q.required}
                            disabled={isFormDisabled}
                          />
                        )}

                        {q.type === "dropdown" && (
                          <Select
                            value={customFieldResponses[q.id] || ""}
                            onValueChange={(value) => setCustomFieldResponses(prev => ({ ...prev, [q.id]: value }))}
                            disabled={isFormDisabled}
                          >
                            <SelectTrigger id={`modify-q-${q.id}`}>
                              <SelectValue placeholder="Select an option" />
                            </SelectTrigger>
                            <SelectContent>
                              {q.options?.map((opt, i) => (
                                <SelectItem key={i} value={opt}>{opt}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}

                        {q.type === "radio" && (
                          <div className="flex flex-col gap-2 pt-1">
                            {(q.options || []).map((opt, i) => (
                              <label key={i} className={cn("flex items-center gap-2 cursor-pointer", isFormDisabled && "opacity-50 cursor-not-allowed")}>
                                <input
                                  type="radio"
                                  name={`modify-radio-${q.id}`}
                                  value={opt}
                                  checked={customFieldResponses[q.id] === opt}
                                  onChange={() => setCustomFieldResponses(prev => ({ ...prev, [q.id]: opt }))}
                                  required={q.required && !customFieldResponses[q.id]}
                                  disabled={isFormDisabled}
                                  className="h-4 w-4 border-input text-primary focus:ring-primary"
                                />
                                <span className="text-sm font-medium">{opt}</span>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Card Footer */}
            <div className="px-6 pb-6 pt-0 flex flex-col sm:flex-row items-center justify-end gap-3 mt-auto">
              <Button
                variant="outline"
                onClick={() => navigate(listings?.businesses?.slug ? `/${listings.businesses.slug}` : "/")}
                className="w-full sm:w-auto"
              >
                Back to home
              </Button>
              <Button
                onClick={handleSubmit}
                className="w-full sm:w-auto shadow-lg shadow-primary/20 hover:shadow-primary/30 transition-all active:scale-[0.98]"
                disabled={isSubmitting || !selectedDate || !selectedTime || isFormDisabled || (!hasChanges && !isCancelled)}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Modifying...
                  </>
                ) : isCancelled ? (
                  "Already cancelled"
                ) : (
                  "Confirm changes"
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={pastDateModalOpen} onOpenChange={setPastDateModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Modification Unavailable</DialogTitle>
            <DialogDescription>
              This booking cannot be modified because the booking date and time has already passed.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="sm:justify-start">
            <Button type="button" variant="outline" onClick={() => setPastDateModalOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={appointmentModalOpen} onOpenChange={setAppointmentModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Reschedule Unavailable Online</DialogTitle>
            <DialogDescription>
              This is an appointment booking, so it can't be rescheduled here.
              <br /><br />
              To change the time or staff member, please cancel this appointment and book a new one, or contact the business directly. You can still cancel below.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="sm:justify-start">
            <Button type="button" variant="outline" onClick={() => setAppointmentModalOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={seriesModalOpen} onOpenChange={setSeriesModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Modification Unavailable</DialogTitle>
            <DialogDescription>
              This booking is part of a recurring series and cannot be modified.
              <br /><br />
              Changing any field on a single occurrence would break the recurrence schedule. If you need a different time or guest count, please cancel this occurrence (or the series) and book again.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="sm:justify-start">
            <Button type="button" variant="outline" onClick={() => setSeriesModalOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={lessThan24hModalOpen} onOpenChange={setLessThan24hModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Modification Unavailable</DialogTitle>
            <DialogDescription>
              Bookings cannot be modified less than 24 hours prior to the booking timeslot.
              <br /><br />
              For more enquiries, contact the business at <a href={`mailto:${booking?.listings?.businesses?.email}`} className="text-primary hover:underline">{booking?.listings?.businesses?.email}</a>.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="sm:justify-start">
            <Button type="button" variant="outline" onClick={() => setLessThan24hModalOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SUCCESS MODAL */}
      <Dialog open={successModalOpen} onOpenChange={() => {}}>
        <DialogContent 
          className="sm:max-w-md"
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>Booking Modified</DialogTitle>
            <DialogDescription>
              Your booking has been successfully modified. An email with the updated details has been sent to your inbox.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="sm:justify-start">
            <Button type="button" onClick={() => {
              window.open('', '_self', '');
              window.close();
            }}>
              Close tab
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ModifyBooking;
